import { useCallback, useEffect, useState } from 'react'

import CircleIcon from '@mui/icons-material/Circle'
import { Grid, Typography, useTheme } from '@mui/material'
import {
  MaterialReactTable,
  createMRTColumnHelper,
  useMaterialReactTable,
} from 'material-react-table'

import CreateButton from 'components/CreateButton'
import HeaderBreadcrumbs from 'components/HeaderBreadcrumb'
import WindowPopUp from 'components/Modelv2'
import { useFetchPartners } from 'hooks/MasterTables'
import useCanAccess from 'hooks/useCanAccess'
import AccessDenied from 'pages/AccessDeniedPage'
import { useAppSelector } from 'store/hooks'
import resources from 'utils/constants/resources'
import { pagination } from 'utils/constants/ui'
import userActions from 'utils/constants/userActions'
import formatDateTime from 'utils/dateTime'
import getDefaultMRTOptions from 'utils/table'

import { IPartner } from '../utils/interface'

import Create from './create'

const resource = resources.PAY_CMS.name

const columnHelper = createMRTColumnHelper<IPartner>()
const defaultMRTOptions = getDefaultMRTOptions<IPartner>()

const Partners = () => {
  const theme = useTheme()
  const canView = useCanAccess({ resource, action: userActions.VIEW })
  const canEdit = useCanAccess({ resource, action: userActions.EDIT })
  const canCreate = useCanAccess({ resource, action: userActions.CREATE })

  const { timezone } = useAppSelector((state) => state.timezone)

  const [enableModal, setEnableModal] = useState<boolean>(false)
  const [selectedPartner, setSelectedPartner] = useState<IPartner | undefined>(
    undefined
  )
  const [paginationState, setPaginationState] = useState({
    pageIndex: pagination.page - 1,
    pageSize: pagination.rowsPerPage,
  })

  const { isLoading, fetchPartners, partnersData } = useFetchPartners()

  const columns = [
    columnHelper.accessor('isActive', {
      header: 'Status',
      size: 50,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ row }) => (
        <CircleIcon
          color={row.original.isActive ? 'success' : 'error'}
          sx={{ width: '0.8rem', marginLeft: '0.5rem' }}
        />
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      size: 150,
    }),
    columnHelper.accessor('createdDate', {
      header: 'Creation time',
      Cell: ({ cell }) => formatDateTime(cell.getValue(), timezone),
    }),
    columnHelper.accessor('createdBy', {
      header: 'Created by',
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Last changed',
      Cell: ({ cell }) => formatDateTime(cell.getValue(), timezone),
    }),
    columnHelper.accessor('updatedBy', {
      header: 'Updated by',
    }),
  ]

  useEffect(() => {
    fetchPartners({
      page: paginationState.pageIndex + 1,
      rowsPerPage: paginationState.pageSize,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationState])

  const handlePaginationChange = useCallback((e: any) => {
    setPaginationState(e)
  }, [])

  const handleModalToggle = useCallback(() => {
    setEnableModal((prev) => !prev)
  }, [])

  const handleCellClick = useCallback((row: IPartner) => {
    setEnableModal(true)
    setSelectedPartner(row)
  }, [])

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns,
    data: partnersData?.data || [],
    rowCount: partnersData?.pagination?.totalRecords || 0,
    state: {
      isLoading,
      pagination: paginationState,
      columnVisibility: { action: !!canEdit },
    },
    onPaginationChange: handlePaginationChange,
    renderTopToolbar: () => (
      <Grid sx={{ padding: theme.spacing(1.25, 2.5) }}>
        <Typography variant="body2" color="text.secondary">
          Showing {partnersData?.pagination?.totalRecords || 0} Partners
        </Typography>
      </Grid>
    ),
    muiTableBodyCellProps: ({ row }) => ({
      onClick: canEdit ? () => handleCellClick(row.original) : undefined,
      style: { cursor: canEdit ? 'pointer' : 'default' },
    }),
  })

  if (!canView) {
    return <AccessDenied />
  }

  return (
    <>
      {enableModal && (
        <WindowPopUp
          open={enableModal}
          setOpen={setEnableModal}
          heading={selectedPartner ? 'Edit Partner' : 'Create Partner'}
          Content={
            <Create
              closeModel={handleModalToggle}
              row={selectedPartner}
              fetchPartners={fetchPartners}
              pageController={paginationState}
            />
          }
        />
      )}
      <HeaderBreadcrumbs
        heading="Partners"
        links={[]}
        action={
          canCreate ? (
            <CreateButton
              resource={resource}
              onCreate={() => {
                handleModalToggle()
                setSelectedPartner(undefined)
              }}
            />
          ) : null
        }
      />
      <Grid width="100%" marginTop={2}>
        <MaterialReactTable table={table} />
      </Grid>
    </>
  )
}

export default Partners
